import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface NotificationPreferences {
  id?: string;
  userId: string;
  emailEnabled: boolean;
  smsEnabled: boolean;
  inAppEnabled: boolean;
  phoneNumber?: string;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone?: string;
}

const NotificationPreferencesPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    userId: user?.id || '',
    emailEnabled: true,
    smsEnabled: false,
    inAppEnabled: true,
    phoneNumber: '',
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
    timezone: user?.timezone || 'UTC',
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Fetch current preferences
  const { data: preferencesData, isLoading } = useQuery({
    queryKey: ['userNotificationPreferences'],
    queryFn: () => apiClient.userNotificationPreferences.get(),
  });

  // Update preferences when data loads
  useEffect(() => {
    if (preferencesData?.data.data) {
      setPreferences(preferencesData.data.data);
    }
  }, [preferencesData]);

  // Save preferences mutation
  const savePreferencesMutation = useMutation({
    mutationFn: (prefs: NotificationPreferences) => 
      apiClient.userNotificationPreferences.update(prefs),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Notification preferences saved successfully!' });
      queryClient.invalidateQueries({ queryKey: ['userNotificationPreferences'] });
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save preferences' });
    },
  });

  const handleSave = () => {
    // Validate phone number if SMS is enabled
    if (preferences.smsEnabled && !preferences.phoneNumber?.trim()) {
      setMessage({ type: 'error', text: 'Phone number is required when SMS notifications are enabled' });
      return;
    }

    // Validate phone number format
    if (preferences.phoneNumber && !/^\+?[\d\s\-\(\)]+$/.test(preferences.phoneNumber)) {
      setMessage({ type: 'error', text: 'Please enter a valid phone number' });
      return;
    }

    savePreferencesMutation.mutate(preferences);
  };

  const handlePhoneNumberChange = (value: string) => {
    // Format phone number as user types
    const formatted = value.replace(/\D/g, ''); // Remove non-digits
    setPreferences({ ...preferences, phoneNumber: formatted });
  };

  const getCommunicationIcon = (type: string) => {
    switch (type) {
      case 'email':
        return <EmailIcon fontSize="small" />;
      case 'sms':
        return <SmsIcon fontSize="small" />;
      case 'in_app':
        return <NotificationsActiveIcon fontSize="small" />;
      default:
        return <NotificationsIcon fontSize="small" />;
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading notification preferences...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Notification Preferences
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Configure how you want to receive alerts and notifications
      </Typography>

      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }} 
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Alert Notifications
          </Typography>
          
          <Grid container spacing={3}>
            {/* Email Notifications */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">Email Notifications</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receive alerts via email
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.emailEnabled}
                      onChange={(e) => setPreferences({ 
                        ...preferences, 
                        emailEnabled: e.target.checked 
                      })}
                    />
                  }
                  label=""
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* SMS Notifications */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <SmsIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">SMS Notifications</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receive alerts via text message
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.smsEnabled}
                      onChange={(e) => setPreferences({ 
                        ...preferences, 
                        smsEnabled: e.target.checked 
                      })}
                    />
                  }
                  label=""
                />
              </Box>
            </Grid>

            {/* Phone Number Input */}
            {preferences.smsEnabled && (
              <Grid item xs={12} sx={{ ml: 4 }}>
                <TextField
                  label="Phone Number"
                  value={preferences.phoneNumber || ''}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  helperText="Enter your phone number for SMS alerts (include country code)"
                  fullWidth
                  required
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* In-App Notifications */}
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <NotificationsActiveIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1">In-App Notifications</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receive alerts within the application
                    </Typography>
                  </Box>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.inAppEnabled}
                      onChange={(e) => setPreferences({ 
                        ...preferences, 
                        inAppEnabled: e.target.checked 
                      })}
                    />
                  }
                  label=""
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Quiet Hours */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Quiet Hours
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set times when you don't want to receive notifications
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={preferences.quietHoursStart || ''}
                    onChange={(e) => setPreferences({ 
                      ...preferences, 
                      quietHoursStart: e.target.value 
                    })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="End Time"
                    type="time"
                    value={preferences.quietHoursEnd || ''}
                    onChange={(e) => setPreferences({ 
                      ...preferences, 
                      quietHoursEnd: e.target.value 
                    })}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Timezone */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={preferences.timezone || 'UTC'}
                  onChange={(e) => setPreferences({ 
                    ...preferences, 
                    timezone: e.target.value 
                  })}
                  label="Timezone"
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="America/New_York">Eastern Time (ET)</MenuItem>
                  <MenuItem value="America/Chicago">Central Time (CT)</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time (MT)</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time (PT)</MenuItem>
                  <MenuItem value="Europe/London">London (GMT)</MenuItem>
                  <MenuItem value="Europe/Paris">Paris (CET)</MenuItem>
                  <MenuItem value="Asia/Tokyo">Tokyo (JST)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={savePreferencesMutation.isPending}
              size="large"
            >
              {savePreferencesMutation.isPending ? 'Saving...' : 'Save Preferences'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Current Settings Summary */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Current Settings Summary
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {preferences.emailEnabled && (
              <Chip 
                icon={<EmailIcon />} 
                label="Email" 
                color="primary" 
                variant="outlined" 
              />
            )}
            {preferences.smsEnabled && (
              <Chip 
                icon={<SmsIcon />} 
                label={`SMS (${preferences.phoneNumber || 'No number'})`} 
                color="primary" 
                variant="outlined" 
              />
            )}
            {preferences.inAppEnabled && (
              <Chip 
                icon={<NotificationsActiveIcon />} 
                label="In-App" 
                color="primary" 
                variant="outlined" 
              />
            )}
            {preferences.quietHoursStart && preferences.quietHoursEnd && (
              <Chip 
                label={`Quiet: ${preferences.quietHoursStart} - ${preferences.quietHoursEnd}`} 
                color="secondary" 
                variant="outlined" 
              />
            )}
            <Chip 
              label={`Timezone: ${preferences.timezone}`} 
              color="default" 
              variant="outlined" 
            />
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default NotificationPreferencesPage;
